import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { Theme, styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQueryClient } from '@tanstack/react-query';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { userKeys } from 'api/queries';
import { Namespace } from 'i18next';
import { useContextMenuState } from 'library/hooks/useContextMenuState';
import { Area } from 'library/models/Area';
import { hasUserAccess } from 'main/auth/Authorization';

import { NewBadge } from './components/NewBadge';
import { TabConfiguration, TabsProps } from './types';

interface ServiceAreaTabsProps extends TabsProps {
	onTabClick?: (tabPath: string) => void;
}

const TabsContainer = styled(Box)(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: 'repeat(4, 1fr)',
	gap: theme.spacing(1.5),
	marginTop: theme.spacing(2),
	marginBottom: theme.spacing(3),
	backgroundColor: theme.palette.grey[50],
	padding: theme.spacing(1, 1, 1, 1),

	[theme.breakpoints.down('md')]: {
		gridTemplateColumns: 'repeat(2, 1fr)',
	},
	[theme.breakpoints.only('xs')]: {
		gridTemplateColumns: '1fr',
	},
}));

const commonButtonStyles = (theme: Theme) => ({
	alignItems: 'center',
	gap: theme.spacing(1.5),
	padding: theme.spacing(1.5, 2),
	borderRadius: theme.spacing(0.5),
	border: `1px solid ${theme.palette.grey[300]}`,
	backgroundColor: 'white',
	'&:hover': {
		borderColor: theme.palette.common.black,
	},
});

const TabButton = styled(NavLink)(({ theme }) => ({
	...commonButtonStyles(theme),
	display: 'flex',
	position: 'relative',
	textDecoration: 'none',
	color: theme.palette.text.primary,
	transition: 'border-color 200ms, background-color 200ms',

	'&:hover': {
		borderBottomWidth: 2,
	},

	'&.active': {
		borderBottom: `2px solid ${theme.palette.common.black}`,

		'&:hover': {
			borderColor: theme.palette.common.black,
		},
	},
}));

const ActiveTabDisplay = styled(ButtonBase)(({ theme }) => ({
	...commonButtonStyles(theme),
	flex: 1,
	borderBottom: `2px solid ${theme.palette.common.black}`,
}));

export const ServiceAreaTabs = memo(
	({ tabs, onTabClick }: ServiceAreaTabsProps) => {
		const { t } = useTranslation<Namespace>();
		const { pathname } = useLocation();
		const queryClient = useQueryClient();
		const isMobile = useMediaQuery((theme: Theme) =>
			theme.breakpoints.only('xs')
		);
		const { open, onClose, onOpen, anchorElement } = useContextMenuState();

		const availableAreas = queryClient.getQueryData<Area[]>(
			userKeys.securityMatrix()
		);

		// filter out tabs the user doesn't have access to, and on mobile only show the active tab in the tab list
		const visibleTabs: TabConfiguration[] = tabs.filter(
			(item) =>
				item.areaSecurity === undefined ||
				hasUserAccess(item.areaSecurity, availableAreas ?? [])
		);

		const activeTab =
			visibleTabs.find((tab) => pathname.includes(tab.path)) ??
			visibleTabs[0];

		return (
			<>
				<TabsContainer data-testid="service-area-tabs">
					{isMobile ? (
						<ActiveTabDisplay
							aria-label="open tabs menu"
							aria-controls={
								open ? 'service-area-tabs-menu' : undefined
							}
							aria-haspopup="true"
							aria-expanded={open ? 'true' : undefined}
							onClick={onOpen}>
							{activeTab.icon}
							<Typography variant="body1" noWrap>
								{t(activeTab.title)}
							</Typography>
							<ExpandMoreIcon
								sx={{ color: 'action.active', ml: 'auto' }}
							/>
						</ActiveTabDisplay>
					) : (
						visibleTabs.map(
							({ icon, path, title, endDateNewBadge }) => (
								<TabButton
									key={path}
									to={path}
									onClick={() => onTabClick?.(path)}
									className={
										pathname.includes(path)
											? 'active'
											: undefined
									}>
									{icon}
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography variant="body1" noWrap>
											{t(title)}
										</Typography>
									</Box>
									<NewBadge
										endDateNewBadge={endDateNewBadge}
									/>
								</TabButton>
							)
						)
					)}
				</TabsContainer>
				<Menu
					id="service-area-tabs-menu"
					anchorEl={anchorElement}
					open={open}
					onClose={onClose}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
					slotProps={{
						paper: {
							style: {
								width: anchorElement?.offsetWidth ?? 'auto',
							},
						},
					}}>
					{visibleTabs.map(
						({ icon, path, title, endDateNewBadge }) => (
							<MenuItem
								key={path}
								selected={pathname.includes(path)}
								sx={{
									padding: 0,
									'& > a': {
										flex: '1 1 auto',
										display: 'flex',
										alignItems: 'center',
										gap: 1.5,
										padding: (theme) =>
											theme.spacing(0.75, 2),
										textDecoration: 'none',
										color: 'text.primary',
									},
								}}>
								<Link
									to={path}
									onClick={() => {
										onClose();
										onTabClick?.(path);
									}}>
									<ListItemIcon>{icon}</ListItemIcon>
									<ListItemText
										slotProps={{
											primary: {
												variant: 'body1',
												noWrap: true,
											},
										}}>
										{t(title)}
									</ListItemText>
									<ListItemIcon>
										<NewBadge
											endDateNewBadge={endDateNewBadge}
										/>
									</ListItemIcon>
								</Link>
							</MenuItem>
						)
					)}
				</Menu>
			</>
		);
	}
);
